import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Profile, updateProfile } from '@/lib/forward-api';
import { useCustomerStore } from '@/stores/customer-store';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface ProfileActionsTabProps {
    profile: Profile;
    onUpdate: () => void;
}

export function ProfileActionsTab({ profile, onUpdate }: ProfileActionsTabProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const navigate = useNavigate();
    const { currentAnswersByCategory, loadAnswersByCategory, answerCategories } = useCustomerStore();

    // Load all answers to find which ones use this profile
    useEffect(() => {
        answerCategories.forEach(cat => {
            if (!currentAnswersByCategory[cat.id]) {
                loadAnswersByCategory(cat.id);
            }
        });
    }, [answerCategories]);

    // Find answers that are forwarding-enabled and use this profile
    const linkedAnswers = useMemo(() => {
        if (!profile.usedByActions) return [];

        const allAnswers = Object.values(currentAnswersByCategory).flat();
        return allAnswers.filter(answer =>
            answer.forwardingEnabled &&
            profile.usedByActions?.includes(answer.id)
        );
    }, [currentAnswersByCategory, profile.usedByActions]);

    const handleToggleErrorHandler = async (enabled: boolean) => {
        try {
            await updateProfile(profile.id, { errorhandler: enabled });
            toast({
                title: t('success.title'),
                description: t('forward.actions.errorHandlerUpdated')
            });
            onUpdate();
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('forward.actions.errorHandlerUpdateError'),
                variant: 'destructive'
            });
        }
    };

    const handleGoToKnowledge = () => {
        navigate('/knowledge');
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{t('forward.actions.forwardSystemErrors')}</span>
                <Switch
                    checked={profile.errorhandler}
                    onCheckedChange={handleToggleErrorHandler}
                />
            </div>

            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">
                        {t('forward.actions.linkedActions', { count: linkedAnswers.length })}
                    </h3>
                    <Button
                        variant="link"
                        onClick={handleGoToKnowledge}
                        className="text-brand-primary h-auto p-0"
                    >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        {t('forward.actions.manageInKnowledge')}
                    </Button>
                </div>

                {linkedAnswers.length === 0 ? (
                    <div className="text-center py-8 border rounded-lg bg-gray-50">
                        <p className="text-sm text-gray-600 mb-2">
                            {t('forward.actions.noLinkedActions')}
                        </p>
                        <p className="text-xs text-gray-500">
                            {t('forward.actions.noLinkedActionsHint')}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {linkedAnswers.map((answer) => (
                            <Collapsible key={answer.id}>
                                <div className="flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-gray-50 transition-colors">
                                    <CollapsibleTrigger className="flex items-center gap-2 flex-1">
                                        <ChevronRight className="h-4 w-4 text-gray-400" />
                                        <span className="font-medium text-sm">{answer.title || t('knowledgePage.untitledAnswer')}</span>
                                    </CollapsibleTrigger>
                                </div>
                                <CollapsibleContent className="px-3 py-2 text-sm text-gray-600 bg-gray-50 rounded-b-lg border-x border-b -mt-1">
                                    {answer.aiResponse || answer.guestMessage || t('forward.actions.noAnswerDefined')}
                                </CollapsibleContent>
                            </Collapsible>
                        ))}
                    </div>
                )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                    <strong>{t('forward.actions.noteTitle')}:</strong> {t('forward.actions.noteDescription')}
                </p>
            </div>
        </div>
    );
}
